import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import "./StudentPortal.css";

import { API_BASE_URL } from "../config/apiBase";

const API = API_BASE_URL;
const TUTOR_NOTE_NOTIFICATION_PREFIX = "Shaanzae left guidance";

function parseTutorGuidanceLessonId(message) {
  const match = String(message || "").match(/Week\s+(\d+)\s*,\s*Lesson\s+(\d+)/i);
  if (!match) return null;
  return `W${match[1]}L${match[2]}`;
}

function isTutorGuidanceNotification(message) {
  return String(message || "")
    .trim()
    .toLowerCase()
    .startsWith(TUTOR_NOTE_NOTIFICATION_PREFIX.toLowerCase());
}

function noteLessonId(note) {
  if (!note) return null;
  if (note.lesson_id) return String(note.lesson_id).toUpperCase();
  if (note.week === null || note.week === undefined || note.week === "" || note.lesson === null || note.lesson === undefined || note.lesson === "") {
    return null;
  }
  return `W${Number(note.week)}L${Number(note.lesson)}`;
}

export default function StudentNotifications({ outletContext } = {}) {
  const navigate = useNavigate();
  const params = useParams();
  const routeOutlet = useOutletContext() || {};
  const outlet = outletContext || routeOutlet;
  const studentId = outlet.studentId || params.id || localStorage.getItem("student_id") || "";
  const closeSidebarPanel = outlet.closeSidebarPanel;
  const sid = useMemo(() => String(studentId || "").toUpperCase(), [studentId]);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [tutorNotes, setTutorNotes] = useState([]);
  const tutorNoteLookup = useMemo(() => {
    const map = new Map();
    for (const note of tutorNotes) {
      const key = noteLessonId(note);
      if (key && !map.has(key)) {
        map.set(key, note);
      }
    }
    return map;
  }, [tutorNotes]);

  const formatTime = (value) => {
    if (!value) return "";
    const raw = String(value);
    const normalized = raw.includes(" ") && !raw.includes("T") ? `${raw.replace(" ", "T")}Z` : raw;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
  };

  useEffect(() => {
    if (!sid) return;
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      try {
        // "All" in practice: cap at 500 to avoid huge payloads.
        const res = await fetch(`${API}/api/notifications/${sid}?limit=500`, {
          cache: "no-store",
        });
        const json = await res.json();
        const list = Array.isArray(json?.notifications) ? json.notifications : [];
        if (!cancelled) setItems(list);

        const hasTutorNoteNotification = list.some((n) => isTutorGuidanceNotification(n?.message));
        if (hasTutorNoteNotification) {
          const notesRes = await fetch(`${API}/api/student/${sid}/tutor-notes`, {
            cache: "no-store",
          });
          const notesJson = await notesRes.json();
          const notes = Array.isArray(notesJson?.notes) ? notesJson.notes : [];
          if (!cancelled) setTutorNotes(notes);
        } else if (!cancelled) {
          setTutorNotes([]);
        }
      } catch (e) {
        if (!cancelled && e?.name !== "AbortError") {
          setItems([]);
          setTutorNotes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAll();
    return () => {
      cancelled = true;
    };
  }, [sid]);

  return (
    <section className="sp-content" aria-label="Notifications">
      <div className="sp-card">
        <div className="sp-panel-header">
          <h2 className="sp-h2">Notifications</h2>
          {closeSidebarPanel ? (
            <button
              type="button"
              className="sl-close-btn sp-panel-close-btn"
              onClick={() => closeSidebarPanel()}
              aria-label="Close notifications panel"
              title="Close notifications panel"
            >
              ×
            </button>
          ) : null}
        </div>
        {loading ? (
          <div className="sp-text">Loading…</div>
        ) : items.length ? (
          <div className="sp-menu-list">
            {items.map((n) => (
              <div
                key={n.id}
                className={`sp-menu-item ${n.is_read ? "" : "unread"} ${isTutorGuidanceNotification(n.message) ? "is-clickable" : ""}`}
                role={isTutorGuidanceNotification(n.message) ? "button" : undefined}
                tabIndex={isTutorGuidanceNotification(n.message) ? 0 : -1}
                onClick={() => {
                  if (!isTutorGuidanceNotification(n.message)) return;
                  if (outlet.goToTutorNotes) {
                    outlet.goToTutorNotes();
                  } else {
                    navigate(`/student/dashboard/${sid}/tutor-notes`);
                  }
                }}
                onKeyDown={(event) => {
                  if (!isTutorGuidanceNotification(n.message)) return;
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  if (outlet.goToTutorNotes) {
                    outlet.goToTutorNotes();
                  } else {
                    navigate(`/student/dashboard/${sid}/tutor-notes`);
                  }
                }}
              >
                <div className="sp-menu-item-msg">{n.message}</div>
                {(() => {
                  const lessonId = parseTutorGuidanceLessonId(n.message);
                  const matchedNote = lessonId ? tutorNoteLookup.get(lessonId) : null;
                  return matchedNote ? (
                    <div className="sp-menu-item-msg" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                      {matchedNote.note}
                    </div>
                  ) : null;
                })()}
                {isTutorGuidanceNotification(n.message) ? (
                  <button
                    type="button"
                    className="sp-cta ghost"
                    style={{ marginTop: 10, alignSelf: "flex-start" }}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (outlet.goToTutorNotes) {
                        outlet.goToTutorNotes();
                      } else {
                        navigate(`/student/dashboard/${sid}/tutor-notes`);
                      }
                    }}
                  >
                    Open Tutor Note
                  </button>
                ) : null}
                {n.created_at ? <div className="sp-menu-item-time">{formatTime(n.created_at)}</div> : null}
              </div>
            ))}
          </div>
        ) : (
          <div className="sp-text">No notifications yet.</div>
        )}
      </div>
    </section>
  );
}
